import { useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  searchGenerators,
  getGeneratorDetail,
  claimGenerator,
  type GeneratorSearchParams,
  type GeneratorSearchResponse,
  type GeneratorDetail,
  type GeneratorClaimResponse,
} from "../../../api/generators";

export function useGeneratorSearch(params: GeneratorSearchParams, enabled = true) {
  const query = useQuery<GeneratorSearchResponse>({
    queryKey: ["generators", "search", params],
    queryFn: () => searchGenerators(params),
    staleTime: 60_000,
    enabled,
  });

  // React Query v5: explicitly refetch when enabled transitions to true
  // without cached data (works around stale disabled→enabled edge case)
  const prevEnabled = useRef(enabled);
  useEffect(() => {
    if (enabled && !prevEnabled.current && !query.data) {
      query.refetch();
    }
    prevEnabled.current = enabled;
  }, [enabled, query.data, query.refetch]);

  return query;
}

export function useGeneratorDetail(plantId: string | null, generatorId: string | null) {
  return useQuery<GeneratorDetail>({
    queryKey: ["generators", "detail", plantId, generatorId],
    queryFn: () => getGeneratorDetail(plantId!, generatorId!),
    enabled: !!plantId && !!generatorId,
  });
}

export function useClaimGenerator() {
  return useMutation<GeneratorClaimResponse, Error, { plantId: string; generatorId: string; token: string }>({
    mutationFn: ({ plantId, generatorId, token }) =>
      claimGenerator(plantId, generatorId, token),
  });
}
